import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";

import "../styles/register.css";
import { friendlyAuthError } from "../lib/friendlyErrors";

function ResetPasswordConfirm() {
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);
  const [canResetPassword, setCanResetPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const markLinkAsValid = useCallback(() => {
    setCanResetPassword(true);
    setMessage("");
    setMessageType("info");
  }, []);

  useEffect(() => {
    const prepareRecoverySession = async () => {
      setCheckingLink(true);

      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      const tokenHash = searchParams.get("token_hash");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const linkType = hashParams.get("type") || searchParams.get("type");

      try {
        if (linkType === "recovery" || code || tokenHash || accessToken) {
          sessionStorage.setItem("passwordRecoveryInProgress", "true");
        }

        if (tokenHash && linkType === "recovery") {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: "recovery",
          });
          if (error) throw error;
          markLinkAsValid();
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          markLinkAsValid();
          return;
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
          markLinkAsValid();
          return;
        }

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        const recoveryInProgress =
          sessionStorage.getItem("passwordRecoveryInProgress") === "true";

        if (error) throw error;

        if (session && (linkType === "recovery" || recoveryInProgress)) {
          markLinkAsValid();
          return;
        }

        setCanResetPassword(false);
        setMessageType("error");
        setMessage("Invalid or expired reset link. Please request a new password reset email.");
      } catch (error) {
        console.error("Password recovery link validation failed:", error);
        setCanResetPassword(false);
        setMessageType("error");
        setMessage(friendlyAuthError(error, "Invalid or expired reset link. Please request a new password reset email."));
      } finally {
        setCheckingLink(false);
      }
    };

    prepareRecoverySession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        sessionStorage.setItem("passwordRecoveryInProgress", "true");
        markLinkAsValid();
        setCheckingLink(false);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [markLinkAsValid]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canResetPassword) {
      setMessageType("error");
      setMessage("Please open the latest password reset link from your email.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessageType("error");
      setMessage("Passwords do not match!");
      return;
    }

    setLoading(true);
    setMessage("");

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error("Password update failed:", error);
      setMessageType("error");
      setMessage(friendlyAuthError(error, "We could not update your password right now. Please try again."));
      setLoading(false);
      return;
    }

    await supabase.auth.signOut();
    sessionStorage.removeItem("passwordRecoveryInProgress");

    setMessageType("success");
    setMessage("Password updated successfully! Redirecting to login...");

    setTimeout(() => {
      navigate("/login", { replace: true });
    }, 2000);

    setLoading(false);
  };

  return (
    <section className="auth-section">
      <div className="auth-box">
        <div className="auth-header">
          <h2>Reset Password</h2>
          <p>
            {checkingLink
              ? "Checking your reset link..."
              : canResetPassword
              ? "Enter your new password below."
              : "Invalid or expired reset link."}
          </p>
        </div>

        {message && (
          <div className={`login-message ${messageType}`}>{message}</div>
        )}

        {canResetPassword && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="field">
              <span>New Password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <span>Confirm New Password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button className="auth-btn" disabled={loading}>
              {loading ? "Updating..." : "Set New Password"}
            </button>

            <div className="auth-footer">
              <a href="/login">Back to Login</a>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

export default ResetPasswordConfirm;
